"use client";

/**
 * VOS DS 2.0 — Kbd: tecla de atalho estilizada (mono, caixinha).
 * `keys` renderiza combinações ("mod" vira ⌘ no Mac / Ctrl no Windows).
 * Elemento de exibição: sem interação própria.
 */
import { type HTMLAttributes } from "react";
import { cn } from "../lib/cn";

const MAC = typeof navigator !== "undefined" && /mac/i.test(navigator.platform || navigator.userAgent);

const SYMBOL: Record<string, string> = {
  mod: MAC ? "⌘" : "Ctrl",
  cmd: "⌘",
  ctrl: MAC ? "⌃" : "Ctrl",
  shift: "⇧",
  alt: MAC ? "⌥" : "Alt",
  enter: "↵",
  esc: "Esc",
  backspace: "⌫",
};

export interface KbdProps extends HTMLAttributes<HTMLElement> {
  /** Combinação, ex.: ["mod", "K"]. Alternativa a `children`. */
  keys?: string[];
  size?: "xs" | "sm";
}

const SIZE: Record<NonNullable<KbdProps["size"]>, string> = {
  xs: "h-[18px] min-w-[18px] px-1 text-[10px]",
  sm: "h-[22px] min-w-[22px] px-1.5 text-[11px]",
};

function Key({ size = "sm", className, children, ...props }: KbdProps) {
  return (
    <kbd
      className={cn(
        "inline-flex select-none items-center justify-center rounded-[5px] border border-[var(--line-2)] bg-[var(--panel-2)] font-mono font-medium text-[var(--ink-3)]",
        SIZE[size],
        className,
      )}
      {...props}
    >
      {children}
    </kbd>
  );
}

export function Kbd({ keys, size = "sm", className, children, ...props }: KbdProps) {
  if (!keys?.length) {
    return (
      <Key size={size} className={className} {...props}>
        {children}
      </Key>
    );
  }
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      {keys.map((k, i) => (
        <Key key={`${k}-${i}`} size={size} {...props}>
          {SYMBOL[k.toLowerCase()] ?? (k.length === 1 ? k.toUpperCase() : k)}
        </Key>
      ))}
    </span>
  );
}
